import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BackButton } from '@/components/common/BackButton';
import { useGetsessionByIdQuery } from '@/store/api/votingApi';
import { Button } from '@/components/ui/button';
import CreateVotingRound from './CreateVotingRound';
import { useState } from 'react';

export function VotingSessionDetails() {
  const { sessionId } = useParams();
  const [openDialog, setOpenDialog] = useState(false);

  const id = Number(sessionId);
  const isValidId = !isNaN(id);

  const { data, isLoading, isError } = useGetsessionByIdQuery(id, {
    skip: !isValidId,
  });

  if (isLoading) {
    return <div className="p-6">Loading session details...</div>;
  }

  if (isError || !data) {
    return <div className="p-6 text-red-500">Failed to load session details</div>;
  }

  const {
    title,
    description,
    startTime,
    endTime,
    round,
    status,
    votings,
    createdRound,
    image,
    rounds,
  } = data;

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center gap-5 mb-4">
        <BackButton />
        <div className="flex justify-between w-full">
          <div>
            <h2 className="text-2xl font-bold">{title}</h2>
            <p className="text-sm text-muted-foreground">Voting Session Details</p>
          </div>
          {createdRound !== round && (
            <Button className="mt-1" onClick={() => setOpenDialog(true)}>
              Add Round
            </Button>
          )}
        </div>
      </div>

      {/* Session Info */}
      <Card>
        <CardHeader>
          <CardTitle>Session Information</CardTitle>
        </CardHeader>

        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="md:col-span-2 flex flex-col gap-1">
            <strong>Image:</strong> <img alt="banner image" src={image || ''} className="h-40" />
          </div>
          <div className="flex flex-col gap-1">
            <strong>Title:</strong> {title}
          </div>

          <div className="flex flex-col gap-1">
            <strong>Rounds:</strong> {createdRound}/{round}
          </div>

          <div className="md:col-span-2 flex flex-col gap-1">
            <strong>Description:</strong> {description}
          </div>

          <div className="flex flex-col gap-1">
            <strong>Start Time:</strong>{' '}
            {startTime && startTime !== 'Invalid Date' ? startTime : '—'}
          </div>

          <div className="flex flex-col gap-1">
            <strong>End Time:</strong> {endTime && endTime !== 'Invalid Date' ? endTime : '—'}
          </div>

          <div>
            <strong>Status:</strong>{' '}
            <Badge variant={status ? 'default' : 'secondary'}>
              {status ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {/* <div>
            <strong>Total Votings:</strong> {votings?.length ?? 0}
          </div> */}
        </CardContent>
      </Card>

      <div className="font-semibold mb-5 text-2xl">Voting Rounds</div>

      {rounds?.length === 0 ? (
        <div className="border border-dashed border-gray-700 rounded-lg p-8 text-center text-gray-400">
          No voting rounds have been created yet. Click <strong>Add Round</strong> to create the
          first voting round for this session.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {rounds.map((roundItem: any) => (
            <Card key={roundItem.id} className="bg-[#0b1220] border-gray-800">
              <CardHeader>
                <CardTitle className="text-lg flex justify-between items-center">
                  {roundItem.title}

                  {/* <Badge variant={roundItem.status ? 'default' : 'secondary'}>
                    {roundItem.status ? 'Active' : 'Inactive'}
                  </Badge> */}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3 text-sm text-gray-300">
                <div>
                  <strong>Image:</strong>
                  <img alt="banner image" src={roundItem.image || ''} className="h-40" />
                </div>
                <div>
                  <strong>Description:</strong>
                  <div className="text-gray-400">{roundItem.description || '—'}</div>
                </div>

                <div>
                  <strong>Contestants:</strong> {roundItem.numberOfContestants}
                </div>

                <div>
                  <strong>Round Number:</strong> {roundItem.roundNumber}
                </div>

                <div>
                  <strong>Created At:</strong> {new Date(roundItem.createdAt).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {openDialog && (
        <CreateVotingRound
          open={openDialog}
          setOpen={setOpenDialog}
          sessionId={Number(sessionId)}
        />
      )}
    </div>
  );
}
